import {type RefObject, useCallback, useEffect, useRef, useState} from 'react';
import type {
    JitsiConference,
    JitsiConnection,
    JitsiLocalTrack,
    JitsiTrack,
    JitsiTrackState,
} from '../pages/stream/types/lib-jitsi-meet';
import useStreamingStore from '../store/useStreamingStore.ts';
import {buildOptions, getCellLayout, MONITOR_BOT_NAME, shouldReceiveByName, STUDIO_NAME} from '../utils/StreamingUtil.ts';
import useCenterStore from '../store/useCenterStore.ts';

interface JitsiConnectionReturn {
    getConnection: () => Promise<void>;
    leave: () => Promise<void>;
    isConnecting: boolean;
    isConnected: boolean;
    studioTrack: JitsiTrackState;
    localTrack: RefObject<JitsiTrackState>;
    localTrackVersion: number;
    remoteTrackVersion: number;
    remoteTracks: RefObject<Map<string, JitsiTrackState>>;
    excludedParticipants: RefObject<Set<string>>;
    displayedParticipants: RefObject<Set<string>>;
    selectedByStudio: RefObject<Set<string>>;
    selectedByStudioOrder: RefObject<Array<string>>;
    excludedByStudio: RefObject<Set<string>>;
    studioStateVersion: number;
    triggerConstraintUpdate: () => void;
}

type TTrackType = 'studio' | 'local' | 'remote';

const PROBE_SLOTS = 4;
const CONSTRAINT_EVAL_INTERVAL = 20000;
const EXCLUDE_RESET_CYCLES = 3;

const useJitsiConnection = (): JitsiConnectionReturn => {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const {centerInfo, mediaStream, videoMode} = useCenterStore();
    const {state, setStreamingState, setIsSplash, setSplashMessage, gridLayout, setGridLayout} = useStreamingStore();
    const studioState = useStreamingStore((s) => s.streamingPollingData?.studio_state);
    const [studioTrack, setStudioTrack] = useState<JitsiTrackState>({partId: '', videoTrack: null, audioTrack: null});
    const localTrack = useRef<JitsiTrackState>({partId: '', videoTrack: null, audioTrack: null});
    const remoteTracks = useRef<Map<string, JitsiTrackState>>(new Map());
    const excludedParticipants = useRef<Set<string>>(new Set());
    const displayedParticipants = useRef<Set<string>>(new Set());
    const selectedByStudio = useRef<Set<string>>(new Set());
    const selectedByStudioOrder = useRef<Array<string>>([]);
    const excludedByStudio = useRef<Set<string>>(new Set());
    const excludedCycleCount = useRef(0);
    const [constraintsTrigger, setConstraintsTrigger] = useState(0);
    const [studioStateVersion, setStudioStateVersion] = useState(0);
    const [localTrackVersion, setLocalTrackVersion] = useState(0);
    const [remoteTrackVersion, setRemoteTrackVersion] = useState(0);

    const updateLocalTracks = useCallback(
        async (newMediaStream: MediaStream) => {
            try {
                if (!state.conference) return;
                if (localTrack.current.videoTrack) {
                    await state.conference.removeTrack(localTrack.current.videoTrack);
                    localTrack.current.videoTrack.dispose();
                    localTrack.current.videoTrack = null;
                }
                if (localTrack.current.audioTrack) {
                    await state.conference.removeTrack(localTrack.current.audioTrack);
                    localTrack.current.audioTrack.dispose();
                    localTrack.current.audioTrack = null;
                }
                const videoTracks = newMediaStream.getVideoTracks();
                const audioTracks = newMediaStream.getAudioTracks();
                let newVideoTrack: JitsiLocalTrack | null = null;
                let newAudioTrack: JitsiLocalTrack | null = null;
                if (videoTracks.length > 0) {
                    newVideoTrack = await window.JitsiMeetJS.createLocalTracks({
                        devices: ['video'],
                        micDeviceId: undefined,
                        cameraDeviceId: videoTracks[0].getSettings().deviceId,
                        resolution: 180,
                    }).then((tracks) => tracks.find((track) => track.getType() === 'video') || null);
                    if (newVideoTrack) {
                        await state.conference.addTrack(newVideoTrack);
                    }
                }
                if (audioTracks.length > 0) {
                    newAudioTrack = await window.JitsiMeetJS.createLocalTracks({
                        devices: ['audio'],
                        micDeviceId: audioTracks[0].getSettings().deviceId,
                        cameraDeviceId: undefined,
                    }).then((tracks) => tracks.find((track) => track.getType() === 'audio') || null);
                    if (newAudioTrack) {
                        await state.conference.addTrack(newAudioTrack);
                        await newAudioTrack.mute();
                    }
                }
                localTrack.current.audioTrack = newAudioTrack;
                localTrack.current.videoTrack = newVideoTrack;
                setLocalTrackVersion((v) => v + 1);
            } catch (error) {
                console.error('로컬 트랙 업데이트 중 오류:', error);
            }
        },
        [state.conference]
    );

    useEffect(() => {
        if (mediaStream && state.conference && isConnected) {
            updateLocalTracks(mediaStream);
        }
    }, [mediaStream, state.conference, isConnected]);

    const updateBridgeConstraints = useCallback(() => {
        if (!state.conference || !isConnected) return;

        const remoteSize = getCellLayout(gridLayout.mode).remoteSize;
        // 레이아웃 전환 중 displayedParticipants가 비어있으면 skip (attachAllTracks 완료 후 재호출됨)
        if (remoteSize > 0 && displayedParticipants.current.size === 0) return;
        const displayedCount = displayedParticipants.current.size;
        // remoteSize > 50인 대규모 그리드는 lastN=-1 (무제한)으로 JVB 포워딩 제한 회피
        const lastN = remoteSize > 50 ? -1 : Math.max(remoteSize, displayedCount) + PROBE_SLOTS;
        const constraints: Record<string, any> = {
            lastN,
            // 그리드 타일은 저해상도 + 저프레임(10fps)로 JVB 대역폭 최소화
            defaultConstraints: {maxHeight: 90, maxFrameRate: 10},
            constraints: {},
        };

        // selectedSources: 스튜디오 + 실제 화면 표출 중인 트랙만
        const selectedSources: string[] = [];
        if (studioTrack.videoTrack) {
            const studioTrackName = studioTrack.videoTrack.getSourceName();
            selectedSources.push(studioTrackName);
            constraints.onStageSources = [studioTrackName];
            constraints.constraints[studioTrackName] = {
                maxHeight: 720,
            };
        }

        const isLargeGrid = remoteSize > 50;
        let ownShare = 0;
        if (isLargeGrid) {
            // 대규모: origin은 own(짝) 몫만 JVB에 미디어 요청(봇이 홀 몫 요청).
            // displayedParticipants는 merge 맵 displayName 키로 오염될 수 있으므로
            // origin 자신의 participantId 키 remoteTracks를 직접 순회한다.
            for (const [partId, trackState] of remoteTracks.current) {
                if (excludedParticipants.current.has(partId)) continue;
                if (!trackState?.videoTrack) continue;
                const name = trackState.displayName ?? '';
                if (!shouldReceiveByName(name, 'own')) continue;
                selectedSources.push(trackState.videoTrack.getSourceName());
                ownShare++;
            }
        } else {
            // 비대규모: 기존 동작 — 화면 표출 중(displayedParticipants)인 트랙만 요청
            for (const partId of displayedParticipants.current) {
                if (excludedParticipants.current.has(partId)) continue;
                const trackState = remoteTracks.current.get(partId);
                if (trackState?.videoTrack) {
                    selectedSources.push(trackState.videoTrack.getSourceName());
                }
            }
        }
        constraints.selectedSources = selectedSources;

        if (isLargeGrid) {
            // [SPLIT_DIAG] 회귀 지표: origin이 요청하는 own(짝) 몫 수.
            // 봇 로그(🤖 [BOT_CONSTRAINTS] selected=)와 합산해 OVERLAP/GAP을 사람이 대조한다.
            const totalRemote = remoteTracks.current.size;
            console.log(`🔀 [SPLIT_DIAG] ownShare=${ownShare}, originTotalRemote=${totalRemote}, displayed=${displayedCount}`);
        }

        // excluded → Bridge에 maxHeight: 0으로 수신 차단
        for (const partId of excludedParticipants.current) {
            const trackState = remoteTracks.current.get(partId);
            if (trackState?.videoTrack) {
                const sourceName = trackState.videoTrack.getSourceName();
                constraints.constraints[sourceName] = {maxHeight: 0, minHeight: 0};
            }
        }

        console.log(
            `🔧 [CONSTRAINTS] lastN=${constraints.lastN}, displayed=${displayedCount}, ` +
            `excluded=${excludedParticipants.current.size}, selected=${selectedSources.length}, remoteSize=${remoteSize}`
        );
        console.log('🔧 [CONSTRAINTS_PAYLOAD]', JSON.stringify(constraints));
        state.conference.setReceiverConstraints(constraints);
    }, [state.conference, isConnected, gridLayout.mode, studioTrack.videoTrack]);

    useEffect(() => {
        updateBridgeConstraints();
    }, [updateBridgeConstraints, constraintsTrigger]);

    // 주기적 제약 업데이트 + excluded 목록 TTL 리셋
    useEffect(() => {
        if (!isConnected || !state.conference) return;
        const interval = setInterval(() => {
            excludedCycleCount.current++;
            if (excludedCycleCount.current >= EXCLUDE_RESET_CYCLES) {
                excludedParticipants.current.clear();
                excludedCycleCount.current = 0;
                console.log('🔄 [CONSTRAINTS] excluded 목록 초기화 - 재평가');
            }
            setConstraintsTrigger((v) => v + 1);
        }, CONSTRAINT_EVAL_INTERVAL);
        return () => clearInterval(interval);
    }, [isConnected, state.conference]);

    const findParticipantIdByDisplayName = useCallback(
        (displayName: string): string | null => {
            if (!state.conference) return null;
            const participants = state.conference.participants as Map<string, {getDisplayName: () => string}>;
            for (const [partId, participant] of participants) {
                if (participant.getDisplayName() === displayName) return partId;
            }
            return null;
        },
        [state.conference]
    );

    // studio_state.ban/selected -> selectedByStudio/excludedByStudio ref 동기화
    // 실제 셀 배치 / receiver constraints 반영은 RemoteCell이 이 ref를 읽어서 처리
    useEffect(() => {
        if (!isConnected || !state.conference || !studioState) return;

        const newExcluded = new Set<string>();
        for (const name of studioState.ban) {
            const partId = findParticipantIdByDisplayName(name);
            if (partId) newExcluded.add(partId);
        }

        const newSelected = new Set<string>();
        const newSelectedOrder: Array<string> = [];
        for (const name of studioState.selected) {
            const partId = findParticipantIdByDisplayName(name);
            if (partId && !newSelected.has(partId)) {
                newSelected.add(partId);
                newSelectedOrder.push(partId);
            }
        }

        const prevExcluded = excludedByStudio.current;
        const excludedChanged =
            newExcluded.size !== prevExcluded.size ||
            [...newExcluded].some((id) => !prevExcluded.has(id));

        const prevSelected = selectedByStudio.current;
        const selectedChanged =
            newSelected.size !== prevSelected.size ||
            [...newSelected].some((id) => !prevSelected.has(id));

        excludedByStudio.current = newExcluded;
        selectedByStudio.current = newSelected;
        selectedByStudioOrder.current = newSelectedOrder;

        if (excludedChanged || selectedChanged) {
            // RemoteCell이 셀 재배치하도록 신호
            setStudioStateVersion((v) => v + 1);
        }
    }, [
        studioState?.ban,
        studioState?.selected,
        isConnected,
        state.conference,
        findParticipantIdByDisplayName,
    ]);

    const handleStudioTrack = useCallback((track: JitsiTrack, participantId: string, trackType: 'video' | 'audio') => {
        if (trackType === 'video') {
            if (studioTrack.videoTrack) return;
            setStudioTrack((prevState) => ({
                ...prevState,
                partId: participantId,
                videoTrack: track,
            }));
            // studioTrack 상태 변경 후 useEffect에서 자동 반영되지만, 즉시 적용을 위해 트리거
            setConstraintsTrigger((v) => v + 1);
        } else {
            if (studioTrack.audioTrack) return;
            setStudioTrack((prevState) => ({
                ...prevState,
                audioTrack: track,
            }));
        }
    }, []);

    const handleLocalTrack = useCallback((track: JitsiTrack, participantId: string, trackType: 'video' | 'audio') => {
        if (trackType === 'video') {
            if (localTrack.current.videoTrack) {
                return;
            }
            localTrack.current.partId = participantId;
            localTrack.current.videoTrack = track;
        } else {
            if (localTrack.current.audioTrack) {
                return;
            }
            localTrack.current.partId = participantId;
            localTrack.current.audioTrack = track;
        }
    }, []);

    const handleRemoteTrack = useCallback((track: JitsiTrack, participantId: string, trackType: 'video' | 'audio', displayName: string) => {
        const foundRemoteTrack = remoteTracks.current.get(participantId);
        if (!foundRemoteTrack) {
            const newTrack: JitsiTrackState = {partId: '', videoTrack: null, audioTrack: null};
            if (trackType === 'video') {
                newTrack.videoTrack = track;
            } else {
                newTrack.audioTrack = track;
            }
            newTrack.partId = participantId;
            newTrack.displayName = displayName;
            remoteTracks.current.set(participantId, newTrack);
        } else {
            if (trackType === 'video') {
                foundRemoteTrack.videoTrack = track;
            } else {
                foundRemoteTrack.audioTrack = track;
            }
            foundRemoteTrack.partId = participantId;
            foundRemoteTrack.displayName = displayName;
        }

        setRemoteTrackVersion((v) => v + 1);
    }, []);

    const handleTrackAdded = useCallback(
        (track: JitsiTrack) => {
            if (!track || typeof track.getType !== 'function' || typeof track.attach !== 'function') {
                console.error('Invalid track object:', track);
                return;
            }

            const trackType = track.getType();
            const participantId = track.getParticipantId();
            const participant = participantId ? track.conference?.participants.get(participantId) : null;
            const displayName = participant?.getDisplayName() || 'Unknown';
            const mediaTrack = (track as any).getTrack?.() as MediaStreamTrack | undefined;

            console.log(
                `🎯 [TRACK_ADDED] type=${trackType}, participantId=${participantId}, ` +
                `displayName="${displayName}", isLocal=${track.isLocal()}, ` +
                `mediaTrack=${mediaTrack ? `${mediaTrack.readyState}(${mediaTrack.id})` : 'null'}, ` +
                `→ ${displayName === '스튜디오' ? 'STUDIO' : track.isLocal() ? 'LOCAL' : 'REMOTE'}`
            );

            if (displayName === STUDIO_NAME) {
                handleStudioTrack(track, participantId!, trackType);
            } else if (displayName === MONITOR_BOT_NAME) {
                // 봇(자기/타 경로당 봇)은 그리드 후보 아님 — 무시
                return;
            } else if (track.isLocal()) {
                handleLocalTrack(track, participantId!, trackType);
            } else {
                handleRemoteTrack(track, participantId!, trackType, displayName);
            }

            if (!track.isLocal() && displayName !== '스튜디오') {
                console.log(`🎯 [REMOTE_TRACKS] 현재 총 ${remoteTracks.current.size}개, ` +
                    `video 있는 트랙: ${Array.from(remoteTracks.current.values()).filter(t => t.videoTrack).length}개`);
            }
        },
        [handleLocalTrack, handleRemoteTrack, handleStudioTrack]
    );

    const clearTrackState = (trackState: TTrackType) => {
        switch (trackState) {
            case 'studio':
                setStudioTrack({partId: '', videoTrack: null, audioTrack: null});
                return;
            case 'local':
                localTrack.current = {partId: '', videoTrack: null, audioTrack: null};
                return;
            case 'remote':
            default:
                return;
        }
    };

    const removeSpecificTrack = (trackState: TTrackType, type: 'video' | 'audio') => {
        switch (trackState) {
            case 'studio':
                if (type === 'video') {
                    setStudioTrack((prevState) => ({
                        ...prevState,
                        videoTrack: null,
                    }));
                } else {
                    setStudioTrack((prevState) => ({
                        ...prevState,
                        audioTrack: null,
                    }));
                }
                return;
            case 'local':
                if (type === 'video') {
                    localTrack.current.videoTrack = null;
                } else {
                    localTrack.current.audioTrack = null;
                }
                return;
            case 'remote':
            default:
                return;
        }
    };

    const handleTrackRemoved = useCallback((track: JitsiTrack) => {
        const participantId = track.getParticipantId();
        const trackType = track.getType();

        if (track.isLocal()) {
            if (localTrack.current.partId === participantId) {
                if (!localTrack.current.videoTrack && !localTrack.current.audioTrack) {
                    clearTrackState('local');
                } else {
                    removeSpecificTrack('local', trackType);
                }
            }
        } else {
            // 스튜디오 트랙인지 확인: displayName으로 판별
            const participant = track.conference?.participants.get(participantId);
            const displayName = participant?.getDisplayName() || 'Unknown';

            if (displayName === '스튜디오') {
                removeSpecificTrack('studio', trackType);
            } else {
                const foundRemoteTrack = remoteTracks.current.get(participantId);
                if (foundRemoteTrack) {
                    if (!foundRemoteTrack.videoTrack && !foundRemoteTrack.audioTrack) {
                        remoteTracks.current.delete(participantId);
                    } else {
                        if (trackType === 'video') {
                            foundRemoteTrack.videoTrack = null;
                        } else {
                            foundRemoteTrack.audioTrack = null;
                        }
                    }
                }
                setRemoteTrackVersion((v) => v + 1);
            }
        }
        track.dispose();
    }, []);

    const createLocalTracks = useCallback(async (conference: JitsiConference) => {
        const {videoInputD, audioInputD, cameraEnable} = useCenterStore.getState();
        const devices: string[] = ['audio'];
        if (cameraEnable) devices.push('video');

        const localTracks = await window.JitsiMeetJS.createLocalTracks({
            devices,
            resolution: 180,
            cameraDeviceId: cameraEnable ? videoInputD?.deviceId : undefined,
            micDeviceId: audioInputD?.deviceId || undefined,
        });
        for (const track of localTracks) {
            await conference.addTrack(track);
            if (track.getType() === 'video') {
                localTrack.current.videoTrack = track;
            } else {
                localTrack.current.audioTrack = track;
                localTrack.current.audioTrack.mute();
            }
        }
        setLocalTrackVersion((v) => v + 1);
        return localTracks;
    }, []);

    const createOnConferenceJoined = useCallback(
        (conference: JitsiConference, username: string) => {
            return async () => {
                setIsConnected(true);
                setIsConnecting(false);
                setSplashMessage('방송 준비가 완료되었습니다!');
                setTimeout(() => setIsSplash(false), 1000);
                if (conference && username) {
                    conference.setDisplayName(username);
                }
                if (conference) {
                    const remoteSize = getCellLayout(useStreamingStore.getState().gridLayout.mode).remoteSize;
                    conference.setReceiverConstraints({
                        lastN: remoteSize + PROBE_SLOTS,
                        defaultConstraints: {maxHeight: 90, maxFrameRate: 10},
                    });
                    // bridge channel open 후 displayedParticipants 기반 constraint 재전송
                    setTimeout(() => {
                        setConstraintsTrigger((v) => v + 1);
                    }, 5000);
                    // mediaStream이 이미 있으면 useEffect의 updateLocalTracks가 처리하므로 스킵
                    const currentMediaStream = useCenterStore.getState().mediaStream;
                    if (!currentMediaStream) {
                        try {
                            await createLocalTracks(conference);
                        } catch (error) {
                            console.error('createOnConferenceJoined 에러', error);
                        }
                    }
                }
            };
        },
        [videoMode, createLocalTracks]
    );

    const onConferenceLeft = useCallback(() => {
        setIsConnected(false);
    }, []);

    const onUserJoined = useCallback((id: string) => {
        console.log('user joined!', id);
    }, []);

    const onUserLeft = useCallback((id: string) => {
        console.log('user left!', id);
    }, []);

    const onMicrophoneChanged = useCallback((isMuted: boolean) => {
        console.log('Microphone changed:', isMuted);
    }, []);

    const onAVModerationChanged = useCallback((isMuted: boolean) => {
        console.log('Mic AV Moderation changed:', isMuted);
    }, []);

    const onAVModerationApproved = useCallback(() => {
        setIsSplash(true);
        setGridLayout({mode: 'direct-chat', sub: true});
        localTrack.current.audioTrack?.unmute();
        setTimeout(() => {
            setIsSplash(false);
        }, 3000);
    }, [localTrack, setIsSplash, setGridLayout, gridLayout]);

    const onTrackMuteChanged = useCallback(
        (track: JitsiTrack) => {
            if (track.isLocal() && track.isMuted()) {
                setGridLayout({mode: 'new-32', sub: true});
                setTimeout(() => {
                    setIsSplash(false);
                }, 3000);
            }
        },
        [setIsSplash, setGridLayout]
    );

    const onParticipantConnStatusChanged = useCallback(
        (id: string, status: string) => {
            console.log(`참가자(${id}) 연결 상태: ${status}`);
        },
        []
    );

    const setupConferenceListeners = useCallback(
        (conference: JitsiConference, username: string) => {
            const onConferenceJoined = createOnConferenceJoined(conference, username);
            conference.on(window.JitsiMeetJS.events.conference.TRACK_ADDED, handleTrackAdded);
            conference.on(window.JitsiMeetJS.events.conference.TRACK_REMOVED, handleTrackRemoved);
            conference.on(window.JitsiMeetJS.events.conference.CONFERENCE_JOINED, onConferenceJoined);
            conference.on(window.JitsiMeetJS.events.conference.CONFERENCE_LEFT, onConferenceLeft);
            conference.on(window.JitsiMeetJS.events.conference.USER_JOINED, onUserJoined);
            conference.on(window.JitsiMeetJS.events.conference.USER_LEFT, onUserLeft);
            conference.on(window.JitsiMeetJS.events.conference.AUDIO_UNMUTE_PERMISSIONS_CHANGED, onMicrophoneChanged);
            conference.on(window.JitsiMeetJS.events.conference.AV_MODERATION_CHANGED, onAVModerationChanged);
            conference.on(window.JitsiMeetJS.events.conference.AV_MODERATION_APPROVED, onAVModerationApproved);
            conference.on(window.JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED, onTrackMuteChanged);
            conference.on(
                window.JitsiMeetJS.events.conference.PARTICIPANT_CONN_STATUS_CHANGED,
                onParticipantConnStatusChanged
            );
        },
        [
            createOnConferenceJoined,
            handleTrackAdded,
            handleTrackRemoved,
            onConferenceLeft,
            onUserJoined,
            onUserLeft,
            onMicrophoneChanged,
            onAVModerationChanged,
            onAVModerationApproved,
            onTrackMuteChanged,
            onParticipantConnStatusChanged,
        ]
    );

    const onConnectionSuccess = useCallback(
        async (connection: JitsiConnection) => {
            try {
                const conference = connection.initJitsiConference(state.room, {
                    userInfo: {
                        displayName: centerInfo?.name || '-',
                    },
                });
                setStreamingState({conference: conference});
                setupConferenceListeners(conference, centerInfo?.name || '-');
                conference.join();
                console.log('컨퍼런스 조인 성공!');
            } catch (error) {
                console.error('Jitsi onConnectionSuccess Fail', error);
                setIsConnecting(false);
            }
        },
        [centerInfo?.name, setStreamingState, setupConferenceListeners]
    );

    const onConnectionFailed = useCallback((error: any) => {
        console.error('Jitsi onConnectionFailed Fail', error);
        setIsConnecting(false);
        setIsConnected(false);
        // 연결 실패 시 connection state 정리
        setStreamingState({
            connection: undefined,
            conference: undefined,
        });
    }, [setStreamingState]);

    const onConnectionDisconnected = useCallback(() => {
        console.log('Jitsi onConnectionDisconnected Fail');
        setIsConnected(false);
        setIsConnecting(false);
        setStreamingState({
            connection: undefined,
            conference: undefined,
        });
    }, [setStreamingState]);

    const getConnection = useCallback(async () => {
        if (isConnecting || isConnected || !state.appId || !state.room || !state.jwt) return;
        try {
            // 기존 연결이 남아있으면 먼저 정리
            if (state.connection) {
                try {
                    state.connection.disconnect();
                } catch (e) {
                    console.warn('기존 연결 정리 중 오류:', e);
                }
                setStreamingState({connection: undefined, conference: undefined});
            }
            setIsConnecting(true);
            const options = buildOptions(state.appId, state.room);
            const connection = new window.JitsiMeetJS.JitsiConnection(null, state.jwt, options);
            connection.addEventListener(window.JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, () =>
                onConnectionSuccess(connection)
            );
            connection.addEventListener(window.JitsiMeetJS.events.connection.CONNECTION_FAILED, onConnectionFailed);
            connection.addEventListener(
                window.JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED,
                onConnectionDisconnected
            );
            setStreamingState({connection});
            connection.connect();
        } catch (error) {
            console.error('Jitsi connect Fail', error);
            setIsConnecting(false);
        }
    }, [
        isConnecting,
        isConnected,
        state.appId,
        state.room,
        state.jwt,
        setStreamingState,
        onConnectionSuccess,
        onConnectionFailed,
        onConnectionDisconnected,
    ]);

    const leave = useCallback(async () => {
        try {
            // 기존 로컬 트랙 dispose
            if (localTrack.current.videoTrack) {
                localTrack.current.videoTrack.dispose();
                localTrack.current.videoTrack = null;
            }
            if (localTrack.current.audioTrack) {
                localTrack.current.audioTrack.dispose();
                localTrack.current.audioTrack = null;
            }

            const {state} = useStreamingStore.getState();
            if (state.conference) {
                await state.conference.leave();
            }
            if (state.connection) {
                state.connection.disconnect();
            }
            setIsConnected(false);
            setIsConnecting(false);
            setStreamingState({
                connection: undefined,
                conference: undefined,
            });
        } catch (error) {
            console.error('Jitsi disconnect Fail', error);
        }
    }, [setStreamingState]);

    const triggerConstraintUpdate = useCallback(() => {
        setConstraintsTrigger((v) => v + 1);
    }, []);

    return {
        getConnection,
        leave,
        isConnecting,
        isConnected,
        studioTrack,
        localTrack,
        localTrackVersion,
        remoteTrackVersion,
        remoteTracks,
        excludedParticipants,
        displayedParticipants,
        selectedByStudio,
        selectedByStudioOrder,
        excludedByStudio,
        studioStateVersion,
        triggerConstraintUpdate,
    };
};

export default useJitsiConnection;
